import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { classificationToChunks, versionToChunks, validateChunks, getOverlapWarning, pdfToBookPage, bookToPdfPage } from '../utils/chunkUtils';

export function useChunkEditor(classification) {
  const pageOffset = classification.page_offset !== undefined ? classification.page_offset : -1;

  const [activeStep, setActiveStep] = useState(0);
  const [localChunks, setLocalChunks] = useState(() => classificationToChunks(classification));
  const [localMetadata, setLocalMetadata] = useState({
    title: classification?.document?.metadata?.title || 'Untitled',
    author: '',
    page_offset: pageOffset,
    eb_hinweise: classification?.document?.metadata?.eb_hinweise || null,
  });
  const [validationError, setValidationError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedChunkId, setSelectedChunkId] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedPageForDialog, setSelectedPageForDialog] = useState(null);
  const [lastSelectedPage, setLastSelectedPage] = useState(null);
  const thumbnailRefs = useRef({});

  // Track whether we're showing classification data or a loaded version
  const [dataSource, setDataSource] = useState('classification');

  useEffect(() => {
    if (localChunks.length > 0 && !selectedChunkId) {
      setSelectedChunkId(localChunks[0].id);
    }
  }, [localChunks, selectedChunkId]);

  useEffect(() => {
    if (selectedChunkId && activeStep === 0) {
      const selectedChunk = localChunks.find(c => c.id === selectedChunkId);
      if (selectedChunk) {
        const pdfPage = bookToPdfPage(selectedChunk.startPage, localMetadata.page_offset);
        if (thumbnailRefs.current[pdfPage]) {
          thumbnailRefs.current[pdfPage].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  }, [selectedChunkId, localChunks, localMetadata.page_offset, activeStep]);

  const validateAndSetError = useCallback(() => {
    const error = validateChunks(localChunks);
    setValidationError(error);
    return !error;
  }, [localChunks]);

  const overlapWarning = useMemo(() => getOverlapWarning(localChunks), [localChunks]);

  const handleUpdateChunkField = useCallback((id, field, value) => {
    setLocalChunks(chunks => chunks.map((c) => {
      if (c.id === id) {
        const updatedChunk = { ...c, [field]: value };
        // If visibility is manually changed, set preserve_visibility to true
        if (field === 'visibility') {
          updatedChunk.preserve_visibility = true;
        }
        return updatedChunk;
      }
      return c;
    }));
  }, []);

  const getPageChunkInfo = useCallback(
    (pdfPageNum) => {
      const bookPage = pdfToBookPage(pdfPageNum, localMetadata.page_offset);
      return localChunks.find((c) => c.startPage <= bookPage && c.endPage >= bookPage);
    },
    [localChunks, localMetadata.page_offset]
  );

  /**
   * Load chunks from a saved version.
   * @param {Object} versionDetail - The full version detail from the API
   */
  const loadVersionChunks = useCallback((versionDetail) => {
    const { chunks, pageOffset: versionPageOffset } = versionToChunks(versionDetail);
    setLocalChunks(chunks);
    setLocalMetadata((prev) => ({ ...prev, page_offset: versionPageOffset }));
    setDataSource('version');
    setSelectedChunkId(chunks[0]?.id || null);
  }, []);

  /**
   * Reset to the original classification data.
   */
  const resetToClassification = useCallback(() => {
    setLocalChunks(classificationToChunks(classification));
    setLocalMetadata((prev) => ({ ...prev, page_offset: pageOffset }));
    setDataSource('classification');
  }, [classification, pageOffset]);

  return {
    activeStep,
    setActiveStep,
    localChunks,
    setLocalChunks,
    localMetadata,
    setLocalMetadata,
    validationError,
    setValidationError,
    saving,
    setSaving,
    selectedChunkId,
    setSelectedChunkId,
    selectedPages,
    setSelectedPages,
    selectedPageForDialog,
    setSelectedPageForDialog,
    lastSelectedPage,
    setLastSelectedPage,
    thumbnailRefs,
    validateAndSetError,
    overlapWarning,
    handleUpdateChunkField,
    getPageChunkInfo,
    dataSource,
    loadVersionChunks,
    resetToClassification,
  };
}
